import {Injectable} from "@angular/core";
import {AuthService} from "../../../services/auth.service";
import {ApiService} from "../../api.service";
import {Observable} from "rxjs";
import {ISettingsDTO} from "../../../../shared/DTOs/ISettingsDTO";
import {map} from "rxjs/operators";
import {SettingsService} from "../../../services/settings.service";
import {Router} from "@angular/router";
import {ESubscriptionStatus} from "../../../../shared/enums/ESubscriptionStatus";
import {ESyncType} from "../../../../shared/enums/ESyncType";
import {IGetAccountInformationResponse} from "./interfaces/get-account-information.response";
import {IUpdatePaymentInfoRequest} from "./interfaces/update-payment-info.request";
import {IChangeAccountInformationRequest} from "./interfaces/change-account-information.request";
import {IGetCountsResponse} from "./interfaces/get-counts.response";
import {IAttemptPaymentRequest} from "./interfaces/attempt-payment.request";
import {IAttemptPaymentResponse} from "./interfaces/attempt-payment.response";
import {GlobalPreferences} from "../../../helpers/global-data";
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import { environment } from "../../../../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class AccountControllerService {
  private readonly _resourceUrl: string = "/account";

  constructor(
    private apiService: ApiService,
    private settingsService: SettingsService,
    private router: Router,
    private global: GlobalPreferences,
    private httpClient: HttpClient
  ) {}

  getSyncState(): Observable<{ syncType: ESyncType }> {
    return this.apiService.get<{ syncType: ESyncType }>(
      `${this._resourceUrl}/sync-state`,
      {},
      AuthService.defaultTokenHeader().headers,
    );
  }

  getLocationCounts(locationId: string): Observable<IGetCountsResponse> {
    return this.apiService.get<IGetCountsResponse>(`${this._resourceUrl}/counts/${locationId}`, {}, {}, AuthService.defaultTokenHeader().headers);

  }

  attemptPayment(payload: IAttemptPaymentRequest = {}): Observable<IAttemptPaymentResponse> {
    let params = new HttpParams();

    if (payload.subscriptionId) {
      params = params.set('subscriptionId', payload.subscriptionId);
    }

    if (payload.isUpdate !== undefined) {
      params = params.set('isUpdate', payload.isUpdate);
    }

    return this.apiService.post<IAttemptPaymentResponse>(
      `${this._resourceUrl}/attempt-payment`,
      {}, // body
      params, // NOT { params }
      AuthService.defaultTokenHeader().headers
    );
  }

  updateSyncState(syncType: ESyncType): Observable<{ syncType: ESyncType }> {
    return this.apiService.patch<{ syncType: ESyncType }>(
      `${this._resourceUrl}/sync-state/${syncType}`,
      {},
      {},
      AuthService.defaultTokenHeader().headers,
    );
  }

  updatePaymentInfo(payload: IUpdatePaymentInfoRequest): Observable<{message: string}> {
    return this.apiService.put<{ message: string }>(
        `${this._resourceUrl}/payment-info`,
        payload,
        {},
        AuthService.defaultTokenHeader().headers,
    );
  }

  register(data): Observable<any> {
    return this.apiService.post(`${this._resourceUrl}/register`, data);
  }

  checkOrganizationName(name: string): Observable<any> {
    return this.apiService.get(`${this._resourceUrl}/check-organization`, {
      name,
    });
  }

  validUser(username: string): Promise<any> {
    return this.apiService
      .get(`${this._resourceUrl}/check-user`, { username })
      .toPromise();
  }

  requestNewPassword(username) {
    return this.apiService.post(
      `${this._resourceUrl}/forgot-password`,
      {},
      { username },
      AuthService.defaultTokenHeader().headers,
    );
  }

  getUserSettings(redirectOnSubscriptionState = true): Observable<ISettingsDTO> {
    return this.apiService.get<ISettingsDTO>(
      `${this._resourceUrl}/settings`,
      {},
      AuthService.defaultTokenHeader().headers,
    ).pipe(map(response => {
      this.settingsService.settings = response;
      localStorage.setItem('settings', JSON.stringify(response));
      const isFreeSubscriptionAllowedRoute =
        this.router.url.includes("/free-subscription") ||
        this.router.url.includes("/upgrade-subscription");
      if(redirectOnSubscriptionState && response?.subscription?.price === 0 && !isFreeSubscriptionAllowedRoute) {
        this.router.navigate(["/free-subscription"]);
      } else if(redirectOnSubscriptionState && response?.locations?.length === 0 && response.subscription.subscriptionStatus !== ESubscriptionStatus.Inactive) {
        this.router.navigate(["/no-location-allowed"], {
          skipLocationChange: true,
        });
      }
      return response;
    }));
  }

  resetPassword(password, token) {
    return this.apiService.put(`${this._resourceUrl}/reset-password`, {
      password: password,
      token: token,
    });
  }

  updateUserPreferences(mandatoryEmail: boolean) {
    let body = {
      emailMandatory: mandatoryEmail,
    };
    return this.apiService.post(
      `${this._resourceUrl}/settings/user-preferences`,
      body,
      {},
      AuthService.defaultTokenHeader().headers,
    );
  }

  updatePassword(data): Observable<any> {
    return this.apiService.put(
      `${this._resourceUrl}/change-password`,
      data,
      {},
      AuthService.defaultTokenHeader().headers,
    );
  }

  changeAccountInformation(data: IChangeAccountInformationRequest): Observable<any> {
    return this.apiService.put(
      `${this._resourceUrl}/account-info`,
      data,
      {},
      AuthService.defaultTokenHeader().headers,
    );
  }

  getAccountInformation(): Observable<IGetAccountInformationResponse> {
    return this.apiService.get(
      `${this._resourceUrl}/account-info`,
      {},
      AuthService.defaultTokenHeader().headers,
    );
  }

  updateProfilePicture(profilePictureUrl): Observable<any> {
    return this.apiService.patch(
      `${this._resourceUrl}/profile-picture`,
      {},
      { profilePictureUrl },
      AuthService.defaultTokenHeader().headers,
    );
  }

  exportOrganizationUsageReport(payload: {
    start: string;
    end: string;
    organizationId: string;
  }): Observable<any> {
    const gmtOffset = this.global.getTimezoneOffset();

    const params = new HttpParams({
      fromObject: {
        ...payload,
        gmtOffset,
      },
    });

    return this.httpClient.get(`${environment.baseURL}${this._resourceUrl}/usage-report-organization`, {
      params: params,
      headers: this.createHeaders(),
      responseType: "blob",
    });
  }

  private createHeaders() {
    return new HttpHeaders()
      .set(
        "Accept",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      )
      .set("Authorization", "Bearer " + AuthService.getOauthTokenFromStorage());
  }
}
